import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useRive } from 'rive-react';

import Link from 'components/shared/link';
import LogosBar from 'components/shared/logos-bar';
import MainContext from 'context/main';

import Form from './form';

const Hero = (props) => {
  const { title, description, linkText, linkUrl, buttonFormText, customersTitle, customersItems } =
    props;
  const {
    language: { locale },
  } = useContext(MainContext);

  const { RiveComponent } = useRive({
    src: '/animations/home/hero.riv',
    stateMachines: 'SM',
    autoplay: true,
  });

  return (
    <section className="hero safe-paddings bg-new-gray-2 pt-12 pb-[120px] overflow-hidden xl:pb-14 xl:pt-5 lg:pt-0 lg:pb-20 md:pt-8 md:pb-16">
      <div className="container">
        <div className="relative flex items-center justify-between xl:flex-col xl:text-center">
          <div
            className={clsx(
              'max-w-[572px] pt-16 pb-[91px] xl:max-w-[680px] sm:max-w-none xl:pb-0 md:pt-1',
              locale === 'ja' && '!max-w-[630px]'
            )}
          >
            <h1 className="text-[64px] text-new-black font-medium leading-tightest -tracking-[0.03em] xl:mx-auto xl:max-w-[610px] xl:text-[56px] xl:tracking-tight-4 xl:leading-[112.5%] lg:text-[48px] md:text-[36px] md:max-w-[300px]">
              {title}
            </h1>
            <p className="mt-5 leading-snug text-xl text-new-gray-1 max-w-[520px] xl:text-center xl:mx-auto xl:mt-4 xl:text-[18px] xl:max-w-full lg:text-[16px] lg:mt-3.5 lg:max-w-[602px] md:mt-3 md:max-w-80">
              {description}
            </p>
            <div
              className={clsx(
                'flex items-center justify-center mt-8 gap-8 xl:mt-6 lg:mt-5 lg:gap-6 md:mt-[38px] md:flex-col md:gap-14',
                locale === 'ca' && 'flex-wrap !justify-start'
              )}
            >
              <Form buttonText={buttonFormText} />
              <Link
                className="flex-shrink-0 xl:text-[16px] lg:text-[14px]"
                theme="secondary-2"
                size="md"
                to={linkUrl}
                withArrow
              >
                {linkText}
              </Link>
            </div>
          </div>

          <div className="pointer-events-none absolute -top-[30px] -right-[47px] xl:relative xl:left-3 xl:top-4 xl:-mb-6 lg:left-1 lg:top-3 md:-top-24 md:left-2 md:-mb-60">
            <span className="absolute rounded-full w-[509px] h-[264px] blur-[90px] rotate-[32.123deg] z-0 bg-secondary-2 opacity-[0.12] top-[45%] left-[-15%] lg:w-[382px] lg:h-50" />
            <span className="absolute rounded-full w-[444px] h-[273px] blur-2xl rotate-[31.554deg] z-0 bg-secondary-1 opacity-[0.05] top-[7%] left-[18%] 2xl:left-[8%] xl:w-[350px] lg:w-[300px] lg:h-[204px] lg:left-[7%]" />
            <RiveComponent className="relative z-10 h-[601px] w-[598px] md:w-[392px]" />
          </div>
        </div>

        {customersItems?.length > 0 && (
          <LogosBar className="mt-10 lg:mt-9" title={customersTitle} items={customersItems} />
        )}
      </div>
    </section>
  );
};

Hero.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  linkText: PropTypes.string.isRequired,
  linkUrl: PropTypes.string.isRequired,
  buttonFormText: PropTypes.string.isRequired,
  customersTitle: PropTypes.string.isRequired,
  customersItems: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
};

export default Hero;
